import { useEffect } from 'react';
import { useAudioStore } from '@/hooks/useAudioStore';
import { isMobileApp } from '@/utils/mobileBridge';

/**
 * Native → Web sync hook.
 *
 * Sluša `suton-native-event` događaje koje SUTON Radio native WebView wrapper
 * (Expo + react-native-track-player) emituje preko `injectJavaScript`. Tipovi:
 *
 *   - `native-play`   → lock screen / Bluetooth / Android Auto play
 *                       → isPlaying = true; ako payload ima stationId+streamUrl,
 *                         sync currentStation (AA stream switch)
 *   - `native-pause`  → pause → isPlaying = false
 *   - `native-stop`   → stop → isPlaying = false (currentStation ostaje)
 *   - `native-error`  → samo logiraj, ne diraj store
 *
 * U PWA / browser mod-u hook je NoOp (early return jer `isMobileApp()` = false).
 *
 * Mora biti pozvan jednom globalno (najbolje u App.tsx pored `useZenoMetadata`,
 * `useAzuraCastMetadata`).
 */

type NativeEventType =
  | 'native-play'
  | 'native-pause'
  | 'native-stop'
  | 'native-error'
  | 'native-eq-result'; // R6.2: native EQ apply result + diagnostics

interface NativeEventDetail {
  type: NativeEventType;
  data?: Record<string, unknown> | null;
}

function isNativeEventDetail(v: unknown): v is NativeEventDetail {
  if (typeof v !== 'object' || v === null) return false;
  const t = (v as { type?: unknown }).type;
  return (
    t === 'native-play' ||
    t === 'native-pause' ||
    t === 'native-stop' ||
    t === 'native-error' ||
    t === 'native-eq-result'
  );
}

/**
 * R6.2: globalni store za posljednji native EQ rezultat (debug area u
 * EqualizerModal sluša ovo). Ne ide u Zustand jer se ne persist-uje i ne
 * potrebuje svuda.
 */
type EqResultListener = (data: Record<string, unknown> | null) => void;
const eqResultListeners = new Set<EqResultListener>();

export function subscribeNativeEqResult(listener: EqResultListener): () => void {
  eqResultListeners.add(listener);
  return () => { eqResultListeners.delete(listener); };
}

type NativeStationPayload = {
  stationId?: string;
  stationName?: string;
  streamUrl?: string;
  artwork?: string | null;
};

function applyNativeStation(data: NativeStationPayload | null | undefined): void {
  if (!data) return;
  const stationId = typeof data.stationId === 'string' ? data.stationId : null;
  const streamUrl = typeof data.streamUrl === 'string' ? data.streamUrl : null;
  const stationName =
    typeof data.stationName === 'string' ? data.stationName : null;
  if (!stationId || !streamUrl) return;

  const store = useAudioStore.getState();
  const prev = store.currentStation;
  const artwork =
    typeof data.artwork === 'string' && data.artwork
      ? data.artwork
      : prev?.id === stationId
        ? prev.artwork
        : '';
  store.setCurrentStation({
    id: stationId,
    name: stationName || prev?.name || 'SUTON Radio',
    streamUrl,
    genre: prev?.id === stationId ? prev.genre : '',
    description: prev?.id === stationId ? prev.description : '',
    artwork,
    isActive: true,
    color: prev?.id === stationId ? prev.color : undefined,
    nameTranslations:
      prev?.id === stationId ? prev.nameTranslations : undefined,
  });
  if (!prev || prev.id !== stationId || prev.streamUrl !== streamUrl) {
    store.setCurrentTrack(null);
  }
}

export function useNativeBridge(): void {
  useEffect(() => {
    if (!isMobileApp()) return;

    // Native injectJavaScript can call this directly (AA stream switch).
    (window as unknown as { __SUTON_APPLY_NATIVE_STATION__?: typeof applyNativeStation })
      .__SUTON_APPLY_NATIVE_STATION__ = applyNativeStation;

    const handler = (event: Event): void => {
      const ce = event as CustomEvent<unknown>;
      const detail = ce.detail;
      if (!isNativeEventDetail(detail)) return;

      const setPlaying = useAudioStore.getState().setPlaying;

      switch (detail.type) {
        case 'native-play': {
          applyNativeStation(detail.data as NativeStationPayload | null);
          setPlaying(true);
          break;
        }
        case 'native-pause':
          setPlaying(false);
          break;
        case 'native-stop':
          // Ne brišemo currentStation — korisnik može odmah resume-ati istu stanicu
          // sa Play dugmeta u UI-u ili sa lock screen-a.
          setPlaying(false);
          break;
        case 'native-error':
          console.warn('[useNativeBridge] native-error:', detail.data);
          break;
        case 'native-eq-result':
          // R6.2: forward na EqualizerModal debug area
          eqResultListeners.forEach((fn) => {
            try { fn(detail.data ?? null); } catch { /* noop */ }
          });
          break;
      }
    };

    window.addEventListener('suton-native-event', handler);
    return () => {
      window.removeEventListener('suton-native-event', handler);
      try {
        delete (window as unknown as { __SUTON_APPLY_NATIVE_STATION__?: unknown })
          .__SUTON_APPLY_NATIVE_STATION__;
      } catch {
        /* noop */
      }
    };
  }, []);
}
